// src/pages/dashboard/Nutrition.tsx
import { useEffect, useState, useMemo } from "react";
import { getSupabase, currentUser } from "@/lib/supabase";
import { ENV } from "@/lib/env";
import { useSubscription } from "@/hooks/useSubscription"; 
import { format, addDays, startOfWeek, isSameDay, parseISO } from "date-fns";
import { 
  Loader2,  
  ChevronLeft, 
  ChevronRight, 
  MoreHorizontal,
  XCircle as CloseIcon,
  CheckCircle,
  X,
  Utensils,
  Edit3,
  Check,
  Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import NutritionPlanLoader from "@/components/NutritionPlanLoader";

// ——————————————————————————————————
// TYPES
// ——————————————————————————————————
interface MealLog {
  id?: string;
  user_id: string;
  meal_id: string;
  date: string;
  meal_type: string;
  completion_percentage: number;
  custom_description?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  source: 'nutrition_plan';
  created_at?: string;
}

interface Meal {
  id: string;
  nutrition_plan_id: string;
  date: string;
  meal_type: string;
  title: string;
  description: string;
  ingredients: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  time?: string;
  completed?: boolean;
}

// ——————————————————————————————————
// CALENDAR STRIP COMPONENT
// ——————————————————————————————————
interface CalendarStripProps {
  currentDate: Date;
  onSelectDate: (date: Date) => void;
  meals: Meal[];
  mealLogs: MealLog[];
  nutritionPlan: any;
}

const CalendarStrip = ({ currentDate, onSelectDate, meals, mealLogs, nutritionPlan }: CalendarStripProps) => {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  
  // Get the Monday of the current week
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  
  // Generate dates for the week
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  
  // Create a map of dates to meals for quick lookup
  const mealsByDate = useMemo(() => {
    const map = new Map<string, Meal[]>();
    meals.forEach(m => {
      if (m.date) {
        const dateKey = format(parseISO(m.date), "yyyy-MM-dd");
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, m]);
      }
    });
    return map;
  }, [meals]);

  // Create a map of dates to logs for quick lookup
  const logsByDate = useMemo(() => {
    const map = new Map<string, MealLog[]>();
    mealLogs.forEach(log => {
      if (log.date) {
        const dateKey = log.date;
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, log]);
      }
    });
    return map;
  }, [mealLogs]);

  // Check completion status for a day
  const getDayStatus = (dateKey: string) => {
    const dayMeals = mealsByDate.get(dateKey) || [];
    const dayLogs = logsByDate.get(dateKey) || [];
    
    if (dayMeals.length === 0) return 'no-meals';
    if (dayLogs.length === 0) return 'not-started';
    if (dayLogs.length >= dayMeals.length) return 'complete';
    return 'partial';
  };

  return (
    <div className="bg-gradient-to-br from-[#f8fafc] to-[#ffffff] rounded-2xl border border-[#e2e8f0] p-4 md:p-6 mb-6 md:mb-8">
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <h4 className="text-xs md:text-sm text-gray-500 uppercase tracking-wider font-semibold">
            {nutritionPlan?.name || "Nutrition Plan"}
          </h4>
        </div>
        <span className="px-3 py-1.5 bg-green-500/15 text-green-400 rounded-full text-xs font-semibold border border-green-500/30">
          {nutritionPlan?.preferences?.dietary_focus?.replace(/_/g, ' ') || "Balanced"}
        </span>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 md:gap-3">
        {weekDates.map((date, i) => {
          const dateKey = format(date, "yyyy-MM-dd");
          const dayMeals = mealsByDate.get(dateKey) || [];
          const isSelected = isSameDay(date, currentDate);
          const hasMeals = dayMeals.length > 0;
          const status = getDayStatus(dateKey);

          return (
            <button
              key={dateKey}
              onClick={() => onSelectDate(date)}
              className={`
                relative p-2 md:p-3 rounded-xl md:rounded-2xl transition-all duration-200
                ${isSelected 
                  ? "bg-green-500/10 border-2 border-green-500" 
                  : status === 'complete'
                    ? "bg-green-500/5 border border-[#e2e8f0]"
                    : status === 'partial'
                      ? "bg-yellow-500/5 border border-[#e2e8f0]"
                      : "bg-[#ffffff] border border-[#e2e8f0] hover:border-[#e2e8f0]"
                }
              `}
            >
              {/* Day Abbreviation */}
              <p className={`
                text-[10px] md:text-xs font-medium mb-1
                ${isSelected ? "text-green-400" : "text-gray-500"}
              `}>
                {days[i]}
              </p>
              
              {/* Date Number */}
              <p className={`
                text-base md:text-xl font-bold
                ${isSelected 
                  ? "text-green-400" 
                  : status === 'complete'
                    ? "text-green-400"
                    : status === 'partial'
                      ? "text-yellow-400"
                      : hasMeals 
                        ? "text-[#0f172a]" 
                        : "text-gray-600"
                }
              `}>
                {format(date, "d")}
              </p>

              {/* Meal Indicator Dot */}
              {hasMeals && (
                <div className={`
                  w-1.5 h-1.5 md:w-2 md:h-2 rounded-full mx-auto mt-1.5 md:mt-2
                  ${status === 'complete' 
                    ? "bg-green-500" 
                    : status === 'partial'
                      ? "bg-yellow-500"
                      : "bg-blue-500"
                  }
                `} />
              )}
            </button>
          );
        })}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e2e8f0]">
        <button
          onClick={() => onSelectDate(addDays(weekStart, -7))}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous Week</span>
        </button>
        
        <p className="text-sm text-gray-500">
          {format(weekStart, "MMM d")} — {format(addDays(weekStart, 6), "MMM d, yyyy")}
        </p>
        
        <button
          onClick={() => onSelectDate(addDays(weekStart, 7))}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition text-sm"
        >
          <span className="hidden sm:inline">Next Week</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ——————————————————————————————————
// MEAL LOGGING MODAL
// ——————————————————————————————————
function MealLogModal({
  meal,
  existingLog,
  onClose,
  onSave,
  isSaving
}: {
  meal: Meal;
  existingLog: MealLog | null;
  onClose: () => void;
  onSave: (log: Partial<MealLog>) => Promise<void>;
  isSaving: boolean;
}) {
  const [percentage, setPercentage] = useState(existingLog?.completion_percentage ?? 100);
  const [useCustom, setUseCustom] = useState(!!existingLog?.custom_description);
  const [customDescription, setCustomDescription] = useState(existingLog?.custom_description || '');
  const [customCalories, setCustomCalories] = useState(existingLog?.custom_description ? existingLog.calories : meal.calories);
  const [customProtein, setCustomProtein] = useState(existingLog?.custom_description ? existingLog.protein : meal.protein);
  const [customCarbs, setCustomCarbs] = useState(existingLog?.custom_description ? existingLog.carbs : meal.carbs);
  const [customFat, setCustomFat] = useState(existingLog?.custom_description ? existingLog.fat : meal.fat);

  // Calculate macros based on percentage
  const calculatedCalories = useCustom ? customCalories : Math.round(meal.calories * (percentage / 100));
  const calculatedProtein = useCustom ? customProtein : Math.round(meal.protein * (percentage / 100));
  const calculatedCarbs = useCustom ? customCarbs : Math.round(meal.carbs * (percentage / 100));
  const calculatedFat = useCustom ? customFat : Math.round(meal.fat * (percentage / 100));

  const percentageOptions = [0, 25, 50, 75, 100];

  const handleSave = async () => {
    await onSave({
      meal_id: meal.id,
      date: meal.date,
      meal_type: meal.meal_type,
      completion_percentage: useCustom ? 100 : percentage,
      custom_description: useCustom ? customDescription : undefined,
      calories: calculatedCalories,
      protein: calculatedProtein,
      carbs: calculatedCarbs,
      fat: calculatedFat,
      source: 'nutrition_plan'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#f8fafc] border-b border-[#e2e8f0] p-4 md:p-6 flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-lg md:text-xl font-bold text-[#0f172a] truncate">{meal.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{meal.meal_type} • {meal.time}</p>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 hover:bg-gray-800 rounded-lg transition flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Toggle: Planned vs Custom */}
          <div className="flex gap-2 p-1 bg-gray-800/50 rounded-xl">
            <button
              onClick={() => setUseCustom(false)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition ${
                !useCustom 
                  ? 'bg-green-500 text-[#0f172a]' 
                  : 'text-gray-500 hover:text-[#0f172a]'
              }`}
            >
              Ate Planned Meal
            </button>
            <button
              onClick={() => setUseCustom(true)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition ${
                useCustom 
                  ? 'bg-blue-500 text-[#0f172a]' 
                  : 'text-gray-500 hover:text-[#0f172a]'
              }`}
            >
              Ate Something Else
            </button>
          </div>

          {!useCustom ? (
            <>
              {/* Percentage Slider */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-3">
                  How much did you eat?
                </label>
                
                {/* Quick Select Buttons */}
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {percentageOptions.map((pct) => (
                    <button
                      key={pct}
                      onClick={() => setPercentage(pct)}
                      className={`py-2 px-2 rounded-lg text-sm font-bold transition ${
                        percentage === pct
                          ? 'bg-green-500 text-[#0f172a]'
                          : 'bg-gray-800 text-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>

                {/* Slider */}
                <div className="relative">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={percentage}
                    onChange={(e) => setPercentage(Number(e.target.value))}
                    className="w-full h-3 bg-gray-800 rounded-full appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:w-6
                      [&::-webkit-slider-thumb]:h-6
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:bg-green-500
                      [&::-webkit-slider-thumb]:cursor-pointer
                      [&::-webkit-slider-thumb]:shadow-lg
                      [&::-moz-range-thumb]:w-6
                      [&::-moz-range-thumb]:h-6
                      [&::-moz-range-thumb]:rounded-full
                      [&::-moz-range-thumb]:bg-green-500
                      [&::-moz-range-thumb]:cursor-pointer
                      [&::-moz-range-thumb]:border-0"
                    style={{
                      background: `linear-gradient(to right, #a78bfa 0%, #a78bfa ${percentage}%, #f1f5f9 ${percentage}%, #f1f5f9 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>None</span>
                    <span>Half</span>
                    <span>All</span>
                  </div>
                </div>

                {/* Visual indicator */}
                <div className="mt-4 text-center">
                  <span className="text-4xl font-bold text-green-400">{percentage}%</span>
                  <p className="text-sm text-gray-500 mt-1">
                    {percentage === 0 && "Skipped this meal"}
                    {percentage > 0 && percentage < 50 && "Had a small portion"}
                    {percentage >= 50 && percentage < 100 && "Had most of it"}
                    {percentage === 100 && "Finished the whole meal"}
                  </p>
                </div>
              </div>

              {/* Planned Meal Reference */}
              <div className="bg-gray-800/30 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-2">Planned meal:</p>
                <p className="text-sm text-gray-600">{meal.description}</p>
              </div>
            </>
          ) : (
            <>
              {/* Custom Meal Input */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  What did you eat instead?
                </label>
                <textarea
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="e.g., Had a chicken salad from the cafeteria..."
                  rows={3}
                  className="w-full p-3 bg-gray-800/50 rounded-xl border border-gray-700 focus:border-blue-500 outline-none resize-none text-sm"
                />
              </div>

              {/* Custom Macros */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-3">
                  Estimated macros (optional)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Calories</label>
                    <input
                      type="number"
                      value={customCalories}
                      onChange={(e) => setCustomCalories(Number(e.target.value))}
                      className="w-full p-2.5 bg-gray-800/50 rounded-lg border border-gray-700 focus:border-blue-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Protein (g)</label>
                    <input
                      type="number"
                      value={customProtein}
                      onChange={(e) => setCustomProtein(Number(e.target.value))}
                      className="w-full p-2.5 bg-gray-800/50 rounded-lg border border-gray-700 focus:border-blue-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Carbs (g)</label>
                    <input
                      type="number"
                      value={customCarbs}
                      onChange={(e) => setCustomCarbs(Number(e.target.value))}
                      className="w-full p-2.5 bg-gray-800/50 rounded-lg border border-gray-700 focus:border-blue-500 outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fat (g)</label>
                    <input
                      type="number"
                      value={customFat}
                      onChange={(e) => setCustomFat(Number(e.target.value))}
                      className="w-full p-2.5 bg-gray-800/50 rounded-lg border border-gray-700 focus:border-blue-500 outline-none text-sm"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Calculated Totals */}
          <div className="bg-gradient-to-br from-green-900/20 to-green-800/10 rounded-xl p-4 border border-green-500/30">
            <p className="text-xs text-gray-500 mb-3">What will be logged:</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-xl md:text-2xl font-bold text-green-400">{calculatedCalories}</p>
                <p className="text-[10px] md:text-xs text-gray-500">Calories</p>
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold text-blue-400">{calculatedProtein}g</p>
                <p className="text-[10px] md:text-xs text-gray-500">Protein</p>
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold text-yellow-400">{calculatedCarbs}g</p>
                <p className="text-[10px] md:text-xs text-gray-500">Carbs</p>
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold text-purple-400">{calculatedFat}g</p>
                <p className="text-[10px] md:text-xs text-gray-500">Fat</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-[#0f172a] font-medium rounded-xl transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || (useCustom && !customDescription.trim())}
              className="flex-1 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Log Meal
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ——————————————————————————————————
// MEAL CARD COMPONENT
// ——————————————————————————————————
function MealCard({ 
  meal, 
  mealLog, 
  onLogClick 
}: { 
  meal: Meal; 
  mealLog: MealLog | null;
  onLogClick: () => void;
}) {
  const isLogged = !!mealLog;
  const loggedPercentage = mealLog?.completion_percentage ?? 0;
  const isCustom = !!mealLog?.custom_description;

  return (
    <div className="w-full bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl md:rounded-3xl shadow-2xl hover:shadow-green-500/20 transition-all duration-300 overflow-hidden border border-gray-800/50">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 md:p-6 border-b border-black/5 gap-3">
        <div className="flex items-center gap-3 md:gap-4">
          {/* Log Status Button */}
          <button
            onClick={onLogClick}
            className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
              isLogged
                ? loggedPercentage === 100
                  ? 'bg-green-500 shadow-lg shadow-green-500/50'
                  : loggedPercentage > 0
                    ? 'bg-yellow-500 shadow-lg shadow-yellow-500/50'
                    : 'bg-red-500 shadow-lg shadow-red-500/50'
                : 'bg-gray-800 hover:bg-gray-700 border-2 border-gray-600 border-dashed'
            }`}
            title={isLogged ? "Edit log" : "Log this meal"}
          >
            {isLogged ? (
              loggedPercentage === 0 ? (
                <X className="w-5 h-5 md:w-6 md:h-6 text-[#0f172a]" strokeWidth={2.5} />
              ) : (
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-[#0f172a]" strokeWidth={2.5} />
              )
            ) : (
              <Utensils className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
            )}
          </button>
          
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-[#0f172a] leading-tight tracking-tight">
                {meal.meal_type || "Meal"}
              </h3>
              {isLogged && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isCustom 
                    ? 'bg-blue-500/20 text-blue-400'
                    : loggedPercentage === 100 
                      ? 'bg-green-500/20 text-green-400'
                      : loggedPercentage > 0
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                }`}>
                  {isCustom ? 'Custom' : `${loggedPercentage}%`}
                </span>
              )}
            </div>
            <p className="text-xs md:text-sm text-gray-500">{meal.time}</p>
          </div>
        </div>

        {/* Quick Log Button (Mobile) */}
        {!isLogged && (
          <button
            onClick={onLogClick}
            className="sm:hidden w-full py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 font-medium rounded-lg transition text-sm"
          >
            + Log Meal
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 py-6 md:py-10 space-y-4 md:space-y-6">
        <div>
          <h4 className="text-lg md:text-xl font-bold text-[#0f172a] mb-2 md:mb-3">{meal.title}</h4>
          <p className="text-gray-600 text-sm md:text-base leading-relaxed">{meal.description}</p>
        </div>

        {/* Show custom description if logged custom meal */}
        {isCustom && mealLog?.custom_description && (
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-3 md:p-4">
            <p className="text-xs text-blue-400 mb-1 font-medium">What you actually ate:</p>
            <p className="text-sm text-gray-600">{mealLog.custom_description}</p>
          </div>
        )}

        {meal.ingredients && (
          <details className="group">
            <summary className="text-sm md:text-base font-semibold text-gray-500 cursor-pointer hover:text-[#0f172a] transition list-none flex items-center gap-2">
              <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
              View Ingredients
            </summary>
            <ul className="list-disc list-inside text-gray-600 space-y-1 mt-3 pl-6 text-sm">
              {meal.ingredients.split('\n').filter(Boolean).map((ing: string, idx: number) => (
                <li key={idx}>{ing.replace(/^[•\-*]\s*/, '')}</li>
              ))}
            </ul>
          </details>
        )}

        {/* Macros - Show logged values if logged, otherwise planned */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          {[
            { 
              label: 'Calories', 
              planned: meal.calories, 
              logged: mealLog?.calories,
              color: 'green'
            },
            { 
              label: 'Protein', 
              planned: meal.protein, 
              logged: mealLog?.protein,
              color: 'blue',
              suffix: 'g'
            },
            { 
              label: 'Carbs', 
              planned: meal.carbs, 
              logged: mealLog?.carbs,
              color: 'yellow',
              suffix: 'g'
            },
            { 
              label: 'Fat', 
              planned: meal.fat, 
              logged: mealLog?.fat,
              color: 'purple',
              suffix: 'g'
            },
          ].map(({ label, planned, logged, color, suffix = '' }) => (
            <div key={label} className="bg-gray-800/50 rounded-xl p-3 md:p-4 text-center">
              {isLogged ? (
                <>
                  <p className={`text-lg md:text-2xl font-bold text-${color}-400`}>
                    {logged}{suffix}
                  </p>
                  {logged !== planned && (
                    <p className="text-[10px] text-gray-500 line-through">
                      {planned}{suffix}
                    </p>
                  )}
                </>
              ) : (
                <p className={`text-lg md:text-2xl font-bold text-${color}-400`}>
                  {planned}{suffix}
                </p>
              )}
              <p className="text-[10px] md:text-xs text-gray-500 mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Log Meal CTA (Desktop) */}
        {!isLogged && (
          <button
            onClick={onLogClick}
            className="hidden sm:flex w-full py-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 font-medium rounded-xl transition items-center justify-center gap-2"
          >
            <Utensils className="w-4 h-4" />
            Log This Meal
          </button>
        )}

        {/* Edit Log Button */}
        {isLogged && (
          <button
            onClick={onLogClick}
            className="w-full py-2 md:py-3 bg-gray-800/50 hover:bg-gray-800 text-gray-500 hover:text-[#0f172a] font-medium rounded-xl transition flex items-center justify-center gap-2 text-sm"
          >
            <Edit3 className="w-4 h-4" />
            Edit Log
          </button>
        )}
      </div>
    </div>
  );
}

// ——————————————————————————————————
// MAIN NUTRITION PAGE
// ——————————————————————————————————
export default function Nutrition() {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [pageDataLoaded, setPageDataLoaded] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [nutritionPlan, setNutritionPlan] = useState<any>(null);
  const [meals, setMeals] = useState<Meal[]>([]);
  const [mealLogs, setMealLogs] = useState<MealLog[]>([]);
  const [allMealLogs, setAllMealLogs] = useState<MealLog[]>([]); // For calendar strip
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPlanMenu, setShowPlanMenu] = useState(false);
  const [showWeeklyIngredients, setShowWeeklyIngredients] = useState(false);
  const [showEditSettings, setShowEditSettings] = useState(false);
  const [editForm, setEditForm] = useState<typeof form | null>(null);
  
  // Meal logging state
  const [logModalMeal, setLogModalMeal] = useState<Meal | null>(null);
  const [isSavingLog, setIsSavingLog] = useState(false);
  const { canAccessPremium, isLoading: subscriptionLoading } = useSubscription();

  const [form, setForm] = useState({
    diet_style: "",
    suggested_daily_calories: "",
    suggested_daily_protein: "",
    suggested_daily_carbs: "",
    suggested_daily_fat: "",
    goal_type: "",
    meals_per_day: 3,
    cooking_time_preference: "30-45 min",
    meal_prep_days: [] as string[],
    breakfast_time: "07:00",
    lunch_time: "12:00",
    dinner_time: "19:00",
    absolute_dislikes: "",
    food_allergies: "",
    preferred_cuisines: [] as string[],
    snack_preferences: "balanced",
    cooking_skill_level: "intermediate",
    kitchen_equipment: [] as string[],
    budget_level: "moderate",
    meal_variety: "moderate_variety",
    lunch_style: "simple_prep",
    work_schedule: "office",
    dietary_focus: "balanced",
  });

  const mealsPerDayOptions = [2, 3, 4, 5, 6];
  const cookingTimeOptions = ["15-30 min", "30-45 min", "45-60 min", "60+ min"];
  const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  
  const cuisineOptions = [
    "Mediterranean", "Asian", "Mexican", "Indian", "Italian", 
    "American", "Middle Eastern", "Thai", "Japanese", "Greek"
  ];

  const snackOptions = [
    { value: "balanced", label: "Balanced (protein + carbs)" },
    { value: "high_protein", label: "High Protein" },
    { value: "low_carb", label: "Low Carb" },
    { value: "fruit_based", label: "Fruit Based" },
    { value: "none", label: "No Snacks" }
  ];

  const skillLevels = [
    { value: "beginner", label: "Beginner (Simple recipes)" },
    { value: "intermediate", label: "Intermediate (Moderate complexity)" },
    { value: "advanced", label: "Advanced (Complex recipes OK)" }
  ];

  const equipmentOptions = [
    "Oven", "Stove", "Microwave", "Slow Cooker", "Air Fryer",
    "Blender", "Food Processor", "Grill", "Instant Pot"
  ];

  const budgetLevels = [
    { value: "budget", label: "Budget Friendly" },
    { value: "moderate", label: "Moderate" },
    { value: "premium", label: "Premium Ingredients OK" }
  ];

  const varietyOptions = [
    { value: "high_variety", label: "High Variety - Different meals every day" },
    { value: "moderate_variety", label: "Moderate - Some repeating meals" },
    { value: "low_variety", label: "Low Variety - Repeat favorites often" },
    { value: "meal_prep", label: "Meal Prep - Same meals for 3-4 days" }
  ];

  const lunchStyleOptions = [
    { value: "full_recipe", label: "Full Recipe - Like dinner" },
    { value: "simple_prep", label: "Simple Prep - Quick assembly" },
    { value: "batch_friendly", label: "Batch Friendly - Make once, eat all week" },
    { value: "grab_and_go", label: "Grab & Go - Minimal prep required" }
  ];

  const workScheduleOptions = [
    { value: "office", label: "Office - 9-5" },
    { value: "remote", label: "Remote Work" },
    { value: "shift", label: "Shift Work" },
    { value: "student", label: "Student" },
    { value: "flexible", label: "Flexible Schedule" }
  ];

  const dietaryFocusOptions = [
    { value: "balanced", label: "Balanced - Everything in moderation" },
    { value: "high_protein", label: "High Protein - Muscle building focus" },
    { value: "low_carb", label: "Low Carb - Reduce carbohydrate intake" },
    { value: "whole_foods", label: "Whole Foods - Minimally processed" },
    { value: "quick_easy", label: "Quick & Easy - Maximum convenience" },
    { value: "performance", label: "Performance - Athletic fuel focus" }
  ];

  // Get current day meals
  const currentDayMeals = meals.filter(
    (m) => m.date && format(new Date(m.date), "yyyy-MM-dd") === format(currentDate, "yyyy-MM-dd")
  );

  // Get meal logs for current day
  const currentDayLogs = mealLogs.filter(
    (log) => log.date === format(currentDate, "yyyy-MM-dd")
  );

  // Get log for a specific meal
  const getLogForMeal = (mealId: string): MealLog | null => {
    return currentDayLogs.find(log => log.meal_id === mealId) || null;
  };

  // Calculate daily totals from LOGGED meals (not planned)
  const calculateDailyTotals = () => {
    if (currentDayLogs.length === 0) return null;
    
    const totals = currentDayLogs.reduce((acc, log) => ({
      calories: acc.calories + (log.calories || 0),
      protein: acc.protein + (log.protein || 0),
      carbs: acc.carbs + (log.carbs || 0),
      fat: acc.fat + (log.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    
    return totals;
  };

  // Calculate planned totals for comparison
  const calculatePlannedTotals = () => {
    if (currentDayMeals.length === 0) return null;
    
    return currentDayMeals.reduce((acc, meal) => ({
      calories: acc.calories + (meal.calories || 0),
      protein: acc.protein + (meal.protein || 0),
      carbs: acc.carbs + (meal.carbs || 0),
      fat: acc.fat + (meal.fat || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const getWeeklyShoppingList = () => {
    const categories = [
      { name: "Proteins", items: [] as any[] },
      { name: "Vegetables", items: [] as any[] },
      { name: "Fruits", items: [] as any[] },
      { name: "Grains & Carbs", items: [] as any[] },
      { name: "Dairy & Eggs", items: [] as any[] },
      { name: "Pantry Staples", items: [] as any[] },
      { name: "Other", items: [] as any[] }
    ];

    const ingredientsMap = new Map<string, number>();
    
    meals.forEach(meal => {
      if (meal.ingredients) {
        const lines = meal.ingredients.split('\n').filter((line: string) => line.trim());
        lines.forEach((line: string) => {
          const cleaned = line.replace(/^[•\-*]\s*/, '').trim();
          if (cleaned) {
            ingredientsMap.set(cleaned, (ingredientsMap.get(cleaned) || 0) + 1);
          }
        });
      }
    });

    Array.from(ingredientsMap.entries()).forEach(([ingredient, count]) => {
      const item = {
        item: ingredient,
        quantity: count > 1 ? `${count}x needed` : "1x needed"
      };

      const lower = ingredient.toLowerCase();
      
      if (lower.includes('chicken') || lower.includes('beef') || lower.includes('pork') || 
          lower.includes('fish') || lower.includes('salmon') || lower.includes('tuna') ||
          lower.includes('turkey') || lower.includes('lamb') || lower.includes('tofu')) {
        categories[0].items.push(item);
      } else if (lower.includes('lettuce') || lower.includes('tomato') || lower.includes('pepper') ||
                 lower.includes('onion') || lower.includes('carrot') || lower.includes('broccoli') ||
                 lower.includes('spinach') || lower.includes('kale') || lower.includes('cucumber')) {
        categories[1].items.push(item);
      } else if (lower.includes('apple') || lower.includes('banana') || lower.includes('berry') ||
                 lower.includes('orange') || lower.includes('grape') || lower.includes('melon')) {
        categories[2].items.push(item);
      } else if (lower.includes('rice') || lower.includes('pasta') || lower.includes('bread') ||
                 lower.includes('quinoa') || lower.includes('oats') || lower.includes('potato')) {
        categories[3].items.push(item);
      } else if (lower.includes('milk') || lower.includes('cheese') || lower.includes('yogurt') ||
                 lower.includes('egg') || lower.includes('butter') || lower.includes('cream')) {
        categories[4].items.push(item);
      } else if (lower.includes('oil') || lower.includes('salt') || lower.includes('pepper') ||
                 lower.includes('spice') || lower.includes('sauce') || lower.includes('vinegar')) {
        categories[5].items.push(item);
      } else {
        categories[6].items.push(item);
      }
    });

    return categories.filter(cat => cat.items.length > 0);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load meal logs when date changes
  useEffect(() => {
    if (nutritionPlan) {
      loadMealLogs();
    }
  }, [currentDate, nutritionPlan]);

  async function loadMealLogs() {
    const user = await currentUser();
    if (!user) return;

    const supabase = getSupabase();
    const dateStr = format(currentDate, "yyyy-MM-dd");

    try {
      // Load from nutrition_plan_logs for detailed meal tracking
      const { data, error } = await supabase
        .from("nutrition_plan_logs")
        .select("*")
        .eq("user_id", user.id)
        .eq("date", dateStr);

      if (error) {
        console.error("[nutrition] Failed to load meal logs:", error);
        return;
      }

      setMealLogs(data || []);
    } catch (err) {
      console.error("[nutrition] Load meal logs error:", err);
    }
  }

  // Load all meal logs for the calendar strip
  async function loadAllMealLogs() {
    const user = await currentUser();
    if (!user) return;

    const supabase = getSupabase();

    try {
      const { data, error } = await supabase
        .from("nutrition_plan_logs")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        console.error("[nutrition] Failed to load all meal logs:", error);
        return;
      }

      setAllMealLogs(data || []);
    } catch (err) {
      console.error("[nutrition] Load all meal logs error:", err);
    }
  }

  async function loadData() {
    setDataLoading(true);
    const user = await currentUser();
    if (!user) {
      setDataLoading(false);
      setPageDataLoaded(true);
      return;
    }

    const supabase = getSupabase();

    try {
      const { data: clientData } = await supabase
        .from("clients")
        .select("diet_style")
        .eq("user_id", user.id)
        .single();

      const { data: goalData } = await supabase
        .from("client_goal_logs")
        .select(`
          suggested_daily_calories,
          suggested_daily_protein,
          suggested_daily_carbs,
          suggested_daily_fat,
          goal_type
        `)
        .eq("user_id", user.id)
        .order("created_date", { ascending: false })
        .limit(1)
        .single();

      setForm(prev => ({
        ...prev,
        diet_style: clientData?.diet_style || "",
        suggested_daily_calories: goalData?.suggested_daily_calories?.toString() || "",
        suggested_daily_protein: goalData?.suggested_daily_protein?.toString() || "",
        suggested_daily_carbs: goalData?.suggested_daily_carbs?.toString() || "",
        suggested_daily_fat: goalData?.suggested_daily_fat?.toString() || "",
        goal_type: goalData?.goal_type || "",
      }));

      const { data: plans } = await supabase
        .from("nutrition_plans")
        .select("*")
        .eq("user_id", user.id)
        .limit(1);

      const plan = plans?.[0] ?? null;
      setNutritionPlan(plan);

      if (plan) {
        const { data: mealsData } = await supabase
          .from("meals")
          .select("*")
          .eq("nutrition_plan_id", plan.id)
          .order("date", { ascending: true });

        setMeals(mealsData || []);
        
        // Load all meal logs for calendar strip
        await loadAllMealLogs();
      }
    } catch (err) {
      console.error("[nutrition] Load error:", err);
    } finally {
      setDataLoading(false);
      setPageDataLoaded(true);
    }
  }

  // Save meal log to both nutrition_plan_logs (for tracking) and daily_calorie_macro_logs (for dashboard)
  const saveMealLog = async (logData: Partial<MealLog>) => {
    const user = await currentUser();
    if (!user) return;

    setIsSavingLog(true);

    try {
      const supabase = getSupabase();
      const dateStr = logData.date!;
      const mealId = logData.meal_id!;
      const meal = logModalMeal;
      
      if (!meal) {
        throw new Error("No meal selected");
      }
      
      // Build the meal name for tracking
      const mealTitle = logData.custom_description || meal.title;
      const mealName = `${logData.meal_type}: ${mealTitle}`;

      // ——————————————————————————————————
      // 1. SAVE TO nutrition_plan_logs (detailed tracking for this page)
      // ——————————————————————————————————
      const existingPlanLog = getLogForMeal(mealId);
      
      const planLogEntry = {
        user_id: user.id,
        meal_id: mealId,
        date: dateStr,
        meal_type: logData.meal_type!,
        completion_percentage: logData.completion_percentage!,
        custom_description: logData.custom_description || null,
        calories: logData.calories!,
        protein: logData.protein!,
        carbs: logData.carbs!,
        fat: logData.fat!,
        source: 'nutrition_plan'
      };

      if (existingPlanLog?.id) {
        const { error } = await supabase
          .from("nutrition_plan_logs")
          .update(planLogEntry)
          .eq("id", existingPlanLog.id);
        if (error) {
          console.warn("[nutrition] Could not update nutrition_plan_logs:", error.message);
        }
      } else {
        const { error } = await supabase
          .from("nutrition_plan_logs")
          .insert(planLogEntry);
        if (error) {
          console.warn("[nutrition] Could not insert to nutrition_plan_logs:", error.message);
        }
      }

      // ——————————————————————————————————
      // 2. SAVE TO daily_calorie_macro_logs (dashboard integration)
      // ——————————————————————————————————
      // Check if there's already a log for this specific meal on this day
      const { data: existingDailyLog } = await supabase
        .from("daily_calorie_macro_logs")
        .select("id")
        .eq("user_id", user.id)
        .gte("created_date", `${dateStr}T00:00:00`)
        .lt("created_date", `${dateStr}T23:59:59`)
        .eq("meal_id", mealId)
        .maybeSingle();

      const dailyLogData = {
        user_id: user.id,
        daily_total_calories: logData.calories!.toString(),
        daily_total_protein_grams: logData.protein!.toString(),
        daily_total_carb_grams: logData.carbs!.toString(),
        daily_total_fat_grams: logData.fat!.toString(),
        meal_name: mealName,
        meal_id: mealId,
        meal_type: logData.meal_type,
        completion_percentage: logData.completion_percentage,
        source: 'nutrition_plan',
      };

      if (existingDailyLog?.id) {
        // Update existing entry for this meal
        const { error } = await supabase
          .from("daily_calorie_macro_logs")
          .update({
            ...dailyLogData,
            modified_date: new Date().toISOString(),
          })
          .eq("id", existingDailyLog.id);

        if (error) {
          console.error("[nutrition] Failed to update daily_calorie_macro_logs:", error);
          throw error;
        }
        console.log("[nutrition] Updated daily log for:", mealName);
      } else {
        // Insert new entry
        const { error } = await supabase
          .from("daily_calorie_macro_logs")
          .insert({
            ...dailyLogData,
            created_date: new Date(`${dateStr}T12:00:00`).toISOString(),
          });

        if (error) {
          console.error("[nutrition] Failed to insert daily_calorie_macro_logs:", error);
          throw error;
        }
        console.log("[nutrition] Created daily log for:", mealName);
      }

      // Refresh logs to update UI
      await loadMealLogs();
      await loadAllMealLogs(); // Refresh calendar strip data
      setLogModalMeal(null);

    } catch (err: any) {
      console.error("[nutrition] Save log error:", err);
      alert("Failed to save meal log: " + err.message);
    } finally {
      setIsSavingLog(false);
    }
  };

  const handleFormChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCuisine = (cuisine: string) => {
    setForm((prev) => {
      const selected = prev.preferred_cuisines.includes(cuisine)
        ? prev.preferred_cuisines.filter((c) => c !== cuisine)
        : [...prev.preferred_cuisines, cuisine];
      return { ...prev, preferred_cuisines: selected.slice(0, 5) };
    });
  };

  const toggleEquipment = (equipment: string) => {
    setForm((prev) => {
      const selected = prev.kitchen_equipment.includes(equipment)
        ? prev.kitchen_equipment.filter((e) => e !== equipment)
        : [...prev.kitchen_equipment, equipment];
      return { ...prev, kitchen_equipment: selected };
    });
  };

  const toggleMealPrepDay = (day: string) => {
    setForm((prev) => {
      const selected = prev.meal_prep_days.includes(day)
        ? prev.meal_prep_days.filter((d) => d !== day)
        : [...prev.meal_prep_days, day];
      return { ...prev, meal_prep_days: selected };
    });
  };

  const createNutritionPlan = async () => {
    if (!form.preferred_cuisines.length) {
      alert("Please select at least one preferred cuisine");
      return;
    }

    setIsGenerating(true);
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      alert("Not authenticated");
      setIsGenerating(false);
      return;
    }

    const user = await currentUser();
    if (!user) {
      setIsGenerating(false);
      return;
    }

    try {
      const res = await fetch(`${ENV.EDGE_BASE}/create-nutrition-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          ...form, 
          user_id: user.id,
          start_date: format(new Date(), "yyyy-MM-dd")
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      
      await loadData();
      alert("Nutrition plan created successfully!");
    } catch (err: any) {
      alert("Failed: " + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!editForm || !nutritionPlan?.id) return;
    
    try {
      const supabase = getSupabase();
      
      const { error } = await supabase
        .from("nutrition_plans")
        .update({
          preferences: {
            meals_per_day: editForm.meals_per_day,
            cooking_time_preference: editForm.cooking_time_preference,
            meal_variety: editForm.meal_variety,
            lunch_style: editForm.lunch_style,
            work_schedule: editForm.work_schedule,
            dietary_focus: editForm.dietary_focus,
            preferred_cuisines: editForm.preferred_cuisines,
            cooking_skill_level: editForm.cooking_skill_level,
            budget_level: editForm.budget_level,
            meal_prep_days: editForm.meal_prep_days,
            absolute_dislikes: editForm.absolute_dislikes,
            food_allergies: editForm.food_allergies,
            snack_preferences: editForm.snack_preferences,
            kitchen_equipment: editForm.kitchen_equipment,
          }
        })
        .eq("id", nutritionPlan.id);

      if (error) throw error;

      setForm(editForm);
      setEditForm(null);
      setShowEditSettings(false);
      alert("Settings saved! When you regenerate your plan, these preferences will be used.");
    } catch (err: any) {
      alert("Failed to save settings: " + err.message);
    }
  };

  if (isPageLoading && !pageDataLoaded) {
  return <NutritionPlanLoader onComplete={() => setIsPageLoading(false)} />;
}

// Subscription loading state
if (subscriptionLoading) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
    </div>
  );
}

// Subscription paywall for cancelled users
if (!canAccessPremium) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="bg-gradient-to-br from-[#f8fafc] to-[#ffffff] rounded-3xl border border-[#e2e8f0] p-8">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-6">
            <Utensils className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-black text-[#0f172a] mb-3">Subscription Required</h1>
          <p className="text-gray-500 mb-6">
            AI Nutrition Plans require an active subscription. Reactivate to create and access your personalized meal plans.
          </p>
          <div className="space-y-3">
            <Link
              to="/billing"
              className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Manage Subscription
            </Link>
            <Link
              to="/dashboard"
              className="flex items-center justify-center w-full py-3 bg-gray-800 hover:bg-gray-700 text-gray-600 font-medium rounded-xl transition"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

  const dailyTotals = calculateDailyTotals();
  const plannedTotals = calculatePlannedTotals();
  const logsCount = currentDayLogs.length;
  const mealsCount = currentDayMeals.length;

  return (
    <>
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
        <main className="flex-1 overflow-y-auto">
          <div className="min-h-screen flex flex-col items-center justify-start p-3 md:p-10 lg:p-12">
            <div className="w-full max-w-4xl mx-auto">
              {!nutritionPlan ? (
                /* ========== CREATE PLAN FORM ========== */
                <div className="mt-6">
                  <div className="mb-6">
                    <Link to="/dashboard" className="flex items-center gap-2 md:gap-3 text-gray-500 hover:text-[#0f172a] transition font-medium w-fit">
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                      <span className="text-sm md:text-base">Back to Dashboard</span>
                    </Link>
                  </div>

                  <div className="text-center mb-8 md:mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-3">Create Your Nutrition Plan</h1>
                    <p className="text-gray-500 text-sm md:text-base lg:text-lg">
                      Personalized meal plan based on your goals and preferences
                    </p>
                  </div>

                  <div className="space-y-6 md:space-y-8">
                    {/* Current Stats */}
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-800/50">
                      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                        <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs md:text-sm font-bold">1</span>
                        Your Current Goals
                      </h2>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Diet Style</label>
                          <input
                            type="text"
                            value={form.diet_style}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="No diet style set"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Goal Type</label>
                          <input
                            type="text"
                            value={form.goal_type}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="No goal set"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Daily Calories</label>
                          <input
                            type="text"
                            value={form.suggested_daily_calories ? `${form.suggested_daily_calories} kcal` : ""}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="Not calculated"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Daily Protein</label>
                          <input
                            type="text"
                            value={form.suggested_daily_protein ? `${form.suggested_daily_protein}g` : ""}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="Not calculated"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Daily Carbs</label>
                          <input
                            type="text"
                            value={form.suggested_daily_carbs ? `${form.suggested_daily_carbs}g` : ""}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="Not calculated"
                          />
                        </div>
                        <div>
                          <label className="block text-xs md:text-sm font-semibold mb-1.5 md:mb-2">Daily Fat</label>
                          <input
                            type="text"
                            value={form.suggested_daily_fat ? `${form.suggested_daily_fat}g` : ""}
                            disabled
                            className="w-full p-3 md:p-4 bg-gray-800/30 rounded-xl border border-gray-700/50 text-gray-600 cursor-not-allowed text-sm md:text-base"
                            placeholder="Not calculated"
                          />
                        </div>
                      </div>
                      
                      <p className="text-[10px] md:text-xs text-gray-500 mt-3 md:mt-4">
                        These values are pulled from your profile. Update them in Settings if needed.
                      </p>
                    </div>

                    {/* Meal Structure */}
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-800/50">
                      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                        <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs md:text-sm font-bold">2</span>
                        Meal Structure
                      </h2>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Meals Per Day</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">How many meals do you want?</p>
                          <select
                            value={form.meals_per_day}
                            onChange={(e) => handleFormChange("meals_per_day", parseInt(e.target.value))}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {mealsPerDayOptions.map((m) => (
                              <option key={m} value={m}>{m} meals</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Cooking Time</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">Max time per meal</p>
                          <select
                            value={form.cooking_time_preference}
                            onChange={(e) => handleFormChange("cooking_time_preference", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {cookingTimeOptions.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="mt-4 md:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Breakfast Time</label>
                          <input
                            type="time"
                            value={form.breakfast_time}
                            onChange={(e) => handleFormChange("breakfast_time", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Lunch Time</label>
                          <input
                            type="time"
                            value={form.lunch_time}
                            onChange={(e) => handleFormChange("lunch_time", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Dinner Time</label>
                          <input
                            type="time"
                            value={form.dinner_time}
                            onChange={(e) => handleFormChange("dinner_time", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Food Preferences */}
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-800/50">
                      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                        <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs md:text-sm font-bold">3</span>
                        Food Preferences
                      </h2>
                      
                      <div className="space-y-4 md:space-y-6">
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">
                            Preferred Cuisines (select up to 5)
                          </label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What types of food do you enjoy?</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                            {cuisineOptions.map((cuisine) => (
                              <button
                                key={cuisine}
                                onClick={() => toggleCuisine(cuisine)}
                                disabled={form.preferred_cuisines.length >= 5 && !form.preferred_cuisines.includes(cuisine)}
                                className={`p-2.5 md:p-3 rounded-xl border transition font-medium text-sm ${
                                  form.preferred_cuisines.includes(cuisine)
                                    ? "bg-green-500 text-[#0f172a] border-green-500"
                                    : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                                } ${form.preferred_cuisines.length >= 5 && !form.preferred_cuisines.includes(cuisine) ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                {cuisine}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Absolute Dislikes</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">Foods you absolutely won't eat</p>
                          <textarea
                            placeholder="e.g., mushrooms, olives, seafood..."
                            value={form.absolute_dislikes}
                            onChange={(e) => handleFormChange("absolute_dislikes", e.target.value)}
                            rows={3}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none resize-none transition text-sm md:text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Allergies & Intolerances</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">Any allergies or dietary restrictions?</p>
                          <textarea
                            placeholder="e.g., lactose intolerant, nut allergy..."
                            value={form.food_allergies}
                            onChange={(e) => handleFormChange("food_allergies", e.target.value)}
                            rows={3}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none resize-none transition text-sm md:text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Snack Preference</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What kind of snacks do you prefer?</p>
                          <select
                            value={form.snack_preferences}
                            onChange={(e) => handleFormChange("snack_preferences", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {snackOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Cooking Details */}
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-800/50">
                      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                        <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs md:text-sm font-bold">4</span>
                        Cooking Details
                      </h2>
                      
                      <div className="space-y-4 md:space-y-6">
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Cooking Skill Level</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">How comfortable are you in the kitchen?</p>
                          <select
                            value={form.cooking_skill_level}
                            onChange={(e) => handleFormChange("cooking_skill_level", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {skillLevels.map((level) => (
                              <option key={level.value} value={level.value}>{level.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Available Equipment</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What do you have in your kitchen?</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                            {equipmentOptions.map((equipment) => (
                              <button
                                key={equipment}
                                onClick={() => toggleEquipment(equipment)}
                                className={`p-2.5 md:p-3 rounded-xl border transition font-medium text-sm ${
                                  form.kitchen_equipment.includes(equipment)
                                    ? "bg-green-500 text-[#0f172a] border-green-500"
                                    : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                                }`}
                              >
                                {equipment}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Budget Level</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What's your grocery budget like?</p>
                          <select
                            value={form.budget_level}
                            onChange={(e) => handleFormChange("budget_level", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {budgetLevels.map((level) => (
                              <option key={level.value} value={level.value}>{level.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Meal Prep Days (Optional)</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">Days you can batch cook</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                            {weekdays.map((day) => (
                              <button
                                key={day}
                                onClick={() => toggleMealPrepDay(day)}
                                className={`p-2.5 md:p-3 rounded-xl border transition font-medium text-sm ${
                                  form.meal_prep_days.includes(day)
                                    ? "bg-green-500 text-[#0f172a] border-green-500"
                                    : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                                }`}
                              >
                                {day.slice(0, 3)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Meal Planning Style */}
                    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl p-4 md:p-6 lg:p-8 border border-gray-800/50">
                      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2 md:gap-3">
                        <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs md:text-sm font-bold">5</span>
                        Meal Planning Style
                      </h2>
                      
                      <div className="space-y-4 md:space-y-6">
                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Meal Variety</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">How much variety do you want?</p>
                          <select
                            value={form.meal_variety}
                            onChange={(e) => handleFormChange("meal_variety", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {varietyOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Lunch Style</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What kind of lunch works for you?</p>
                          <select
                            value={form.lunch_style}
                            onChange={(e) => handleFormChange("lunch_style", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {lunchStyleOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Work Schedule</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What's your typical schedule?</p>
                          <select
                            value={form.work_schedule}
                            onChange={(e) => handleFormChange("work_schedule", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {workScheduleOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm md:text-base font-semibold mb-1.5 md:mb-2">Dietary Focus</label>
                          <p className="text-[10px] md:text-xs text-gray-500 mb-2 md:mb-3">What's most important to you?</p>
                          <select
                            value={form.dietary_focus}
                            onChange={(e) => handleFormChange("dietary_focus", e.target.value)}
                            className="w-full p-3 md:p-4 bg-gray-800/50 rounded-xl border border-gray-700 hover:border-gray-600 focus:border-green-500 outline-none transition text-sm md:text-base"
                          >
                            {dietaryFocusOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* AI Disclaimer */}
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-2xl p-4 md:p-6">
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="text-2xl md:text-3xl">⚠️</div>
                        <div className="flex-1">
                          <h3 className="text-base md:text-lg font-bold text-yellow-400 mb-1 md:mb-2">Important Disclaimer</h3>
                          <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                            This meal plan is AI-generated and should be used as a guide only. AI can make mistakes.
                            <strong className="text-[#0f172a]"> Always consult with a registered dietitian or healthcare professional before making significant changes to your diet</strong>.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={createNutritionPlan}
                      disabled={isGenerating || !form.preferred_cuisines.length}
                      className="relative w-full py-3 md:py-4 bg-green-500 hover:bg-green-400 disabled:opacity-60 disabled:cursor-not-allowed text-[#0f172a] font-bold text-base md:text-lg rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 md:gap-3"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating Plan...
                        </>
                      ) : (
                        "Create My Nutrition Plan"
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                /* ========== NUTRITION PLAN VIEW ========== */
                <>
                  <div className="mb-4 md:mb-6">
                    <Link to="/dashboard" className="flex items-center gap-2 md:gap-3 text-gray-500 hover:text-[#0f172a] transition font-medium w-fit">
                      <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                      <span className="text-sm md:text-base">Back to Dashboard</span>
                    </Link>
                  </div>


                  {/* Menu */}
                  <div className="relative flex justify-center mb-6 md:mb-8">
                    <button
                      onClick={() => setShowPlanMenu(!showPlanMenu)}
                      className="p-2 md:p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition text-gray-500 hover:text-[#0f172a]"
                      aria-label="Plan options"
                    >
                      <MoreHorizontal className="w-5 h-5 md:w-6 md:h-6" />
                    </button>

                    {showPlanMenu && (
                      <div className="absolute top-full right-0 mt-2 bg-[#f8fafc] border border-gray-700 rounded-xl shadow-xl z-20 min-w-[160px] overflow-hidden">
                        <button
                          onClick={() => {
                            setShowPlanMenu(false);
                            setShowDeleteConfirm(true);
                          }}
                          className="w-full px-4 md:px-5 py-2.5 md:py-3 text-left text-red-400 hover:bg-red-900/30 transition flex items-center gap-2 md:gap-3 text-sm md:text-base"
                        >
                          <CloseIcon className="w-4 h-4 md:w-5 md:h-5" />
                          Delete Plan
                        </button>
                      </div>
                    )}
                  </div>

                    {/* ========== CALENDAR STRIP NAVIGATION ========== */}
                  <CalendarStrip
                    currentDate={currentDate}
                    onSelectDate={setCurrentDate}
                    meals={meals}
                    mealLogs={allMealLogs}
                    nutritionPlan={nutritionPlan}
                  />

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-2 md:gap-4 mb-6 md:mb-8">
                    <button
                      onClick={() => {
                        setEditForm(form);
                        setShowEditSettings(true);
                      }}
                      className="px-4 md:px-6 py-2.5 md:py-3 bg-blue-600 hover:bg-blue-500 text-[#0f172a] font-bold rounded-xl transition-all shadow-lg text-sm md:text-base"
                    >
                      ⚙️ Edit Settings
                    </button>
                    <button
                      onClick={() => setShowWeeklyIngredients(true)}
                      className="px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-[#0f172a] font-bold rounded-xl transition-all shadow-lg hover:shadow-xl text-sm md:text-base"
                    >
                      📋 Shopping List
                    </button>
                  </div>

                  {/* Daily Progress Summary */}
                  {currentDayMeals.length > 0 && (
                    <div className="mb-6 md:mb-8 bg-gradient-to-br from-green-900/20 to-green-800/10 rounded-2xl p-4 md:p-6 border border-green-500/30">
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <div>
                          <h3 className="text-lg md:text-xl font-bold">{format(currentDate, "EEEE")}</h3>
                          <p className="text-sm text-gray-500">{format(currentDate, "MMMM d, yyyy")}</p>
                        </div>
                        <span className="text-xs md:text-sm text-gray-500">
                          {logsCount}/{mealsCount} meals logged
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden mb-4">
                        <div 
                          className="h-full bg-green-500 transition-all duration-500"
                          style={{ width: `${mealsCount > 0 ? (logsCount / mealsCount) * 100 : 0}%` }}
                        />
                      </div>

                      {/* Totals Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                        {[
                          { label: 'Calories', logged: dailyTotals?.calories || 0, planned: plannedTotals?.calories || 0, color: 'green' },
                          { label: 'Protein', logged: dailyTotals?.protein || 0, planned: plannedTotals?.protein || 0, color: 'blue', suffix: 'g' },
                          { label: 'Carbs', logged: dailyTotals?.carbs || 0, planned: plannedTotals?.carbs || 0, color: 'yellow', suffix: 'g' },
                          { label: 'Fat', logged: dailyTotals?.fat || 0, planned: plannedTotals?.fat || 0, color: 'purple', suffix: 'g' },
                        ].map(({ label, logged, planned, color, suffix = '' }) => (
                          <div key={label} className="text-center">
                            <p className={`text-xl md:text-3xl font-bold text-${color}-400`}>
                              {logged}{suffix}
                            </p>
                            <p className="text-[10px] md:text-xs text-gray-500">
                              / {planned}{suffix} {label.toLowerCase()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Meals Display */}
                  {currentDayMeals.length > 0 ? (
                    <div className="space-y-4 md:space-y-8">
                      {currentDayMeals.map((meal, i) => (
                        <MealCard
                          key={meal.id || i}
                          meal={meal}
                          mealLog={getLogForMeal(meal.id)}
                          onLogClick={() => setLogModalMeal(meal)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16 md:py-24">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Utensils className="w-8 h-8 md:w-10 md:h-10 text-gray-600" />
                      </div>
                      <p className="text-gray-500 text-lg md:text-xl font-medium">No Meals Planned</p>
                      <p className="text-gray-500 text-sm md:text-base mt-2">
                        No meals scheduled for {format(currentDate, "EEEE, MMMM d")}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Meal Log Modal */}
      {logModalMeal && (
        <MealLogModal
          meal={logModalMeal}
          existingLog={getLogForMeal(logModalMeal.id)}
          onClose={() => setLogModalMeal(null)}
          onSave={saveMealLog}
          isSaving={isSavingLog}
        />
      )}

      {/* Weekly Shopping List Modal */}
      {showWeeklyIngredients && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#f8fafc] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700">
            <div className="sticky top-0 bg-[#f8fafc] border-b border-gray-700 p-4 md:p-6 flex items-center justify-between">
              <h3 className="text-xl md:text-2xl font-bold">Weekly Shopping List</h3>
              <button
                onClick={() => setShowWeeklyIngredients(false)}
                className="p-2 hover:bg-gray-800 rounded-full transition"
              >
                <CloseIcon className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>
            
            <div className="p-4 md:p-6">
              <p className="text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
                Everything you need for your 7-day meal plan
              </p>
              
              <div className="space-y-4 md:space-y-6">
                {getWeeklyShoppingList().map((category, catIdx) => (
                  <div key={catIdx}>
                    <h4 className="text-base md:text-lg font-bold text-green-400 mb-2 md:mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-400"></span>
                      {category.name}
                    </h4>
                    <div className="space-y-2">
                      {category.items.map((item: any, itemIdx: number) => (
                        <div
                          key={itemIdx}
                          className="flex items-start gap-3 p-2.5 md:p-3 bg-gray-800/50 rounded-xl hover:bg-gray-800 transition"
                        >
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-[10px] md:text-xs font-bold mt-0.5 flex-shrink-0">
                            ✓
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-600 font-medium text-sm md:text-base truncate">{item.item}</p>
                            <p className="text-gray-500 text-xs md:text-sm">{item.quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => {
                  const categories = getWeeklyShoppingList();
                  const shoppingListText = categories
                    .map(cat => 
                      `${cat.name.toUpperCase()}\n` +
                      cat.items.map((item: any) => `☐ ${item.item} - ${item.quantity}`).join('\n')
                    )
                    .join('\n\n');
                  navigator.clipboard.writeText(shoppingListText);
                  alert('Shopping list copied to clipboard!');
                }}
                className="w-full mt-4 md:mt-6 py-2.5 md:py-3 bg-green-600 hover:bg-green-500 text-[#0f172a] font-bold rounded-xl transition text-sm md:text-base"
              >
                📋 Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#f8fafc] p-6 md:p-8 rounded-2xl max-w-md w-full text-center border border-gray-700">
            <h3 className="text-xl md:text-2xl font-bold text-red-400 mb-3 md:mb-4">Delete Nutrition Plan?</h3>
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
              This will permanently delete your nutrition plan and all meals. This cannot be undone.
            </p>
            <div className="flex justify-center gap-3 md:gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 md:px-6 py-2.5 md:py-3 bg-gray-700 hover:bg-gray-600 text-[#0f172a] font-medium rounded-xl transition text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!nutritionPlan?.id) return;
                  try {
                    const supabase = getSupabase();
                    await supabase.from("nutrition_plans").delete().eq("id", nutritionPlan.id);
                    await supabase.from("meals").delete().eq("nutrition_plan_id", nutritionPlan.id);
                    
                    setNutritionPlan(null);
                    setMeals([]);
                    setShowDeleteConfirm(false);
                    alert("Nutrition plan deleted successfully.");
                  } catch (err: any) {
                    alert(`Failed to delete: ${err.message}`);
                  }
                }}
                className="px-4 md:px-6 py-2.5 md:py-3 bg-red-600 hover:bg-red-700 text-[#0f172a] font-bold rounded-xl transition shadow-lg text-sm md:text-base"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Settings Modal */}
      {showEditSettings && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#f8fafc] rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="sticky top-0 bg-[#f8fafc] border-b border-gray-700 p-4 md:p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="text-xl md:text-2xl font-bold">Edit Meal Plan Settings</h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Settings will be saved for future plan generation</p>
              </div>
              <button
                onClick={() => {
                  setShowEditSettings(false);
                  setEditForm(null);
                }}
                className="p-2 hover:bg-gray-800 rounded-full transition"
              >
                <CloseIcon className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-4 md:space-y-6">
              {/* Meal Structure Settings */}
              <div className="bg-gray-800/30 rounded-xl p-4 md:p-6">
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">Meal Structure</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 md:mb-2">Meals Per Day</label>
                    <select
                      value={editForm?.meals_per_day || form.meals_per_day}
                      onChange={(e) => setEditForm({...(editForm || form), meals_per_day: parseInt(e.target.value)})}
                      className="w-full p-2.5 md:p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm md:text-base"
                    >
                      {mealsPerDayOptions.map((m) => (
                        <option key={m} value={m}>{m} meals</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 md:mb-2">Cooking Time</label>
                    <select
                      value={editForm?.cooking_time_preference || form.cooking_time_preference}
                      onChange={(e) => setEditForm({...(editForm || form), cooking_time_preference: e.target.value})}
                      className="w-full p-2.5 md:p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm md:text-base"
                    >
                      {cookingTimeOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Meal Planning Style */}
              <div className="bg-gray-800/30 rounded-xl p-4 md:p-6">
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">Meal Planning Style</h4>
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 md:mb-2">Meal Variety</label>
                    <select
                      value={editForm?.meal_variety || form.meal_variety}
                      onChange={(e) => setEditForm({...(editForm || form), meal_variety: e.target.value})}
                      className="w-full p-2.5 md:p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm md:text-base"
                    >
                      {varietyOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 md:mb-2">Lunch Style</label>
                    <select
                      value={editForm?.lunch_style || form.lunch_style}
                      onChange={(e) => setEditForm({...(editForm || form), lunch_style: e.target.value})}
                      className="w-full p-2.5 md:p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm md:text-base"
                    >
                      {lunchStyleOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 md:mb-2">Dietary Focus</label>
                    <select
                      value={editForm?.dietary_focus || form.dietary_focus}
                      onChange={(e) => setEditForm({...(editForm || form), dietary_focus: e.target.value})}
                      className="w-full p-2.5 md:p-3 bg-gray-800 rounded-lg border border-gray-700 text-sm md:text-base"
                    >
                      {dietaryFocusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Update Cuisines */}
              <div className="bg-gray-800/30 rounded-xl p-4 md:p-6">
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">Preferred Cuisines (max 5)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                  {cuisineOptions.map((cuisine) => {
                    const currentCuisines = editForm?.preferred_cuisines || form.preferred_cuisines;
                    const isSelected = currentCuisines.includes(cuisine);
                    return (
                      <button
                        key={cuisine}
                        onClick={() => {
                          const updated = isSelected
                            ? currentCuisines.filter((c) => c !== cuisine)
                            : [...currentCuisines, cuisine].slice(0, 5);
                          setEditForm({...(editForm || form), preferred_cuisines: updated});
                        }}
                        disabled={!isSelected && currentCuisines.length >= 5}
                        className={`p-2.5 md:p-3 rounded-xl border transition font-medium text-sm ${
                          isSelected
                            ? "bg-green-500 text-[#0f172a] border-green-500"
                            : "bg-gray-800/50 border-gray-700 hover:border-green-500"
                        } ${!isSelected && currentCuisines.length >= 5 ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {cuisine}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-3 md:p-4">
                <p className="text-xs md:text-sm text-blue-200">
                  💡 <strong>Note:</strong> Your current meal plan will stay as-is. These settings will be used for next week or when you create a new plan.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 md:gap-4 pt-2 md:pt-4">
                <button
                  onClick={() => {
                    setShowEditSettings(false);
                    setEditForm(null);
                  }}
                  className="flex-1 py-2.5 md:py-3 bg-gray-700 hover:bg-gray-600 text-[#0f172a] font-medium rounded-xl transition text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="flex-1 py-2.5 md:py-3 bg-green-600 hover:bg-green-500 text-[#0f172a] font-bold rounded-xl transition shadow-lg text-sm md:text-base"
                >
                  💾 Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 z-50">
          <NutritionPlanLoader onComplete={() => {}} />
        </div>
      )}
    </>
  );
}
